import {
    Component,
    ElementRef,
    ViewChild,
    OnInit,
    Input,
    Output,
    EventEmitter,
    HostBinding,
} from '@angular/core';
import { Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

import { SnackbarService } from '../../../core/services/snackbar.service';
import { GeneralService } from '../../../core/services/general.service';
import { FilterOutputModel, FiltersEnum } from '../../../core/models/models';
import { SimulationDefinitionModel } from '../../../core/models/simulation-steps.models';
import { FormControl } from '@angular/forms';

export class CustomMatTableDataSource<T> extends MatTableDataSource<T> {
    _filterData(data: T[]) {
        // overriding _filterData to get filterPredicate execute even if filter value is empty
        this.filteredData = data.filter((obj) =>
            this.filterPredicate(obj, this.filter)
        );

        if (this.paginator) {
            this._updatePaginator(this.filteredData.length);
        }

        return this.filteredData;
    }
}

// Abnormal table fetch all data at once
// pagination is done on client side
@Component({
    selector: 'abnormal-table',
    template: '',
    styleUrls: ['../main-table.component.scss'],
})
export class AbnormalTableComponent implements OnInit {
    // func to get pagin size from another table
    @Input()
    set customPageSize(value: number) {
        if (value === this.pageSize) {
            return;
        }
        this.pageSize = value;
        // change pagination pageSize to emit page event
        this.paginator._changePageSize(this.pageSize);
    }

    constructor(
        public snackbar: SnackbarService,
        public generalService: GeneralService,
        public router: Router
    ) {
        this.dataSource.data = [];
        // this.idFieldName = this.columnsData.shift().columnDef;
        // this.displayedColumns = this.getDisplayColumns();
    }
    @HostBinding('class') classes = 'flex flex-grow';

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    @ViewChild('filter') filter: ElementRef;

    @Input() columnsData: Array<any>;
    @Input() selectFilterEnum = FiltersEnum;
    @Input() isLoadingResults = false;
    @Input() tableTitle: string = null;
    @Output() customPageSizeChange = new EventEmitter();

    // pagination settings
    pageIndex = 0;
    pageSize = 25;
    lowValue = 0;
    highValue = 25;
    sortBy = 'Id';

    // id field to edit, first in line in columnData
    idFieldName: string;
    displayedColumns: Array<string>;
    filterValue = '';
    itemsLength = 0;
    pageEvent: PageEvent;
    selFilterValue: FilterOutputModel[] = [];

    filterControl = new FormControl('');

    loadSubscription: Subscription;

    dataSource = new CustomMatTableDataSource<any>();

    // apply plain text filter
    // if filter appeared in any of column -> true
    private static applyPlainFilter(el, filter: string): boolean {
        if (filter) {
            for (const obj in el) {
                if (el.hasOwnProperty(obj) && el[obj]) {
                    if (
                        el[obj].toString().trim().toLowerCase().includes(filter)
                    ) {
                        return true;
                    }
                }
            }
            return false;
        }
        return true;
    }

    ngOnInit() {
        this.initDataSource();
    }

    protected initDataSource(): void {
        this.dataSource.paginator = this.paginator;

        // custom function for filtering dataSource
        // custom function is necessary because two ways to filter:
        // plain filter (input field) and select filter (drop modal)
        this.dataSource.filterPredicate = (data: any, filter: string) =>
            this.applyFilters(data, filter);
    }

    protected getDisplayColumns(): string[] {
        return this.columnsData.map((c) => c.columnDef);
    }

    public clearFilter() {
        this.filterControl.setValue('');
        this.applyFilter('');
    }

    public filterByTrigger() {
        this.applyFilter(this.filter.nativeElement.value);
    }

    getPaginatorData(event: PageEvent): PageEvent {
        this.pageSize = event.pageSize;
        this.setCustomPageSize(event.pageSize);
        if (event.pageIndex === this.pageIndex + 1) {
            this.lowValue = this.lowValue + this.pageSize;
            this.highValue = this.highValue + this.pageSize;
        } else if (event.pageIndex === this.pageIndex - 1) {
            this.lowValue = this.lowValue - this.pageSize;
            this.highValue = this.highValue - this.pageSize;
        }
        this.pageIndex = event.pageIndex;

        return event;
    }

    // inner function to change pagin size and emit change to parent component
    private setCustomPageSize(value: number) {
        this.pageSize = value;
        this.customPageSizeChange.emit(this.pageSize);
    }

    public selectSortName(column: any): string {
        return column.sortColumnDef ? column.sortColumnDef : column.columnDef;
    }

    applyFilter(value: string) {
        this.dataSource.filter = value.trim().toLowerCase();
    }

    public sortChange(e: { selectedName: string; reversed: boolean }) {
        this.sortBy = e.reversed ? e.selectedName + ' desc' : e.selectedName;
        if (e.reversed) {
            this.dataSource.data = this.dataSource.data.sort((a, b) =>
                a[e.selectedName] < b[e.selectedName] ? 1 : -1
            );
        } else {
            this.dataSource.data = this.dataSource.data.sort((a, b) =>
                a[e.selectedName] > b[e.selectedName] ? 1 : -1
            );
        }
    }

    public selFilterChanged(event: FilterOutputModel): void {
        const filter = this.selFilterValue.find(
            (el) => el.filterName === event.filterName
        );
        // if filters key was already in, then we should override else push
        if (
            filter &&
            !!filter.filterValues.length &&
            !!event.filterValues.length
        ) {
            filter.filterValues = event.filterValues;
        } else if (!event.filterValues.length) {
            const indexFilter = this.selFilterValue.findIndex(
                (fltr) => fltr.filterName === event.filterName
            );
            this.selFilterValue.splice(indexFilter, 1);
        } else {
            this.selFilterValue.push(event);
        }

        this.dataSource.filter = this.dataSource.filter;
    }

    // function to apply both filter and select filter
    private applyFilters(el, filter: string): boolean {
        this.dataSource.paginator.firstPage();
        return (
            AbnormalTableComponent.applyPlainFilter(el, filter) &&
            this.applySelectFilter(el)
        );
    }

    private applySelectFilter(el: any): boolean {
        // apply select filters
        // if column includes one of select filter -> true
        const selFiltersNames = this.selFilterValue.map((el) => el.filterName);
        for (const name of selFiltersNames) {
            const nameValues = this.selFilterValue.find(
                (el) => el.filterName === name
            ).filterValues;
            const elValue = el[this.selectFilterEnum[name]]
                ? el[this.selectFilterEnum[name]].toString()
                : el[this.selectFilterEnum[name]];
            if (!nameValues.includes(elValue)) {
                return false;
            }
        }
        return true;
    }

    public checkIfSelected(colName: string) {
        return this.sortBy.indexOf(colName) >= 0 ? true : false;
    }

    public getTooltip(column: Object, row: any): string {
        try {
            switch (column['type']) {
                case 'date':
                    return row ? this.generalService.parseDate(row) : row;
                case 'vat':
                    return row
                        ? this.generalService.parseVAT(row).toFixed(0)
                        : row;
                case 'margin':
                    return row
                        ? this.generalService.parseVAT(row).toFixed(1)
                        : row;
                default:
                    break;
            }
            switch (column['suffix']) {
                case 'currency':
                    return row ? row.toFixed(2) : row;
                    break;
                default:
                    return row;
                    break;
            }
        } catch (e) {
            console.log(e);
            console.log('error in type');
        }
    }
}
